
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { CreditCard, Send, Building2, TestTube, Zap } from "lucide-react";

const FinanceManagement = () => {
  const { toast } = useToast();
  const [isTransferring, setIsTransferring] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [sandboxMode, setSandboxMode] = useState(true);

  // Get providers with Stripe Connect accounts
  const { data: providers, isLoading } = useQuery({
    queryKey: ['providers-with-stripe'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('providers')
        .select('id, name, slug, stripe_connect_account_id')
        .not('stripe_connect_account_id', 'is', null)
        .order('name');

      if (error) throw error;
      return data;
    },
  });

  const handleTransfer = async () => {
    if (!selectedProvider || !amount || !description) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    const provider = providers?.find(p => p.id === selectedProvider);
    if (!provider?.stripe_connect_account_id) {
      toast({
        title: "Error",
        description: "Selected provider does not have a valid Stripe Connect account.",
        variant: "destructive",
      });
      return;
    }

    setIsTransferring(true);

    try {
      const { data, error } = await supabase.functions.invoke('create-stripe-transfer', {
        body: {
          amount: Math.round(parseFloat(amount) * 100), // Convert to pence
          destination: provider.stripe_connect_account_id,
          description: description,
          providerId: provider.id,
          sandboxMode: sandboxMode,
        }
      });

      if (error) throw error;

      toast({
        title: "Transfer Successful",
        description: `£${amount} has been transferred to ${provider.name} ${sandboxMode ? '(Sandbox Mode)' : '(Live Mode)'}.`,
      });

      // Reset form
      setSelectedProvider("");
      setAmount("");
      setDescription("");

    } catch (error) {
      console.error('Transfer error:', error);
      toast({
        title: "Transfer Failed",
        description: error.message || "Failed to process the transfer. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsTransferring(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Loading providers...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <CreditCard className="h-5 w-5" />
            <span>Stripe Transfer to Connected Account</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Sandbox Mode Toggle */}
          <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border">
            <div className="flex items-center space-x-2">
              {sandboxMode ? (
                <TestTube className="h-5 w-5 text-orange-600" />
              ) : (
                <Zap className="h-5 w-5 text-green-600" />
              )}
              <Label htmlFor="sandbox-mode" className="font-medium">
                {sandboxMode ? "Sandbox Mode" : "Live Mode"}
              </Label>
            </div>
            <Switch
              id="sandbox-mode"
              checked={sandboxMode}
              onCheckedChange={setSandboxMode}
            />
            <div className="text-sm text-gray-600">
              {sandboxMode 
                ? "Using test Stripe keys - no real money will be transferred" 
                : "Using live Stripe keys - real money will be transferred"
              }
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="provider">Provider *</Label>
              <Select value={selectedProvider} onValueChange={setSelectedProvider}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a provider" />
                </SelectTrigger>
                <SelectContent>
                  {providers?.map((provider) => (
                    <SelectItem key={provider.id} value={provider.id}>
                      <div className="flex items-center space-x-2">
                        <Building2 className="h-4 w-4" />
                        <span>{provider.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {providers?.length === 0 && (
                <p className="text-sm text-gray-500">
                  No providers with Stripe Connect accounts found.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="amount">Amount (£) *</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter a description for this transfer (e.g., 'Goodwill gesture for booking issue')"
              rows={3}
            />
          </div>

          <div className={`border rounded-lg p-4 ${
            sandboxMode 
              ? "bg-orange-50 border-orange-200" 
              : "bg-yellow-50 border-yellow-200"
          }`}>
            <p className={`text-sm ${
              sandboxMode 
                ? "text-orange-800" 
                : "text-yellow-800"
            }`}>
              <strong>Important:</strong> {sandboxMode 
                ? "This is sandbox mode - no real money will be transferred. Use this for testing purposes only."
                : "This will transfer real funds from the main platform balance to the selected provider's Stripe Connect account. This action cannot be undone."
              }
            </p>
          </div>

          <Button 
            onClick={handleTransfer} 
            disabled={isTransferring || !selectedProvider || !amount || !description}
            className="w-full"
            variant={sandboxMode ? "outline" : "default"}
          >
            <Send className="h-4 w-4 mr-2" />
            {isTransferring 
              ? "Processing Transfer..." 
              : `Send Transfer ${sandboxMode ? '(Sandbox)' : '(Live)'}`
            }
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default FinanceManagement;
